'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppImage from '@/components/ui/AppImage';

const navLinks = [
{ label: 'Home', href: '/homepage', icon: '🏠' },
{ label: 'Work', href: '/portfolio', icon: '🎨' },
{ label: 'Services', href: '/services', icon: '⚙️' },
{ label: 'About', href: '/about', icon: '👤' },
{ label: 'Contact', href: '/contact', icon: '✉️' }];


export default function NotFound() {
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      if (query.includes('work') || query.includes('portfolio') || query.includes('project')) {
        router.push('/portfolio');
      } else if (query.includes('service') || query.includes('brand') || query.includes('web') || query.includes('design')) {
        router.push('/services');
      } else if (query.includes('about') || query.includes('story') || query.includes('team') || query.includes('mission')) {
        router.push('/about');
      } else if (query.includes('contact') || query.includes('hire') || query.includes('talk') || query.includes('start')) {
        router.push('/contact');
      } else {
        router.push('/homepage');
      }
    }
  };

  return (
    <div className="relative min-h-screen flex flex-col bg-[#351129] text-white overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <AppImage
          src="https://img.rocket.new/generatedImages/rocket_gen_img_1f977937c-1772129382091.png"
          alt="Industrial urban environment with raw concrete textures and bold street art"
          width={1920}
          height={1080}
          className="w-full h-full object-cover opacity-30" />
        
        <div className="absolute inset-0 bg-gradient-to-br from-[#351129]/90 via-[#351129]/75 to-[#351129]/90" />
      </div>

      {/* Header bar */}
      <div className="relative z-10 flex items-center justify-between px-4 sm:px-8 py-4 border-b border-white/10">
        <Link href="/homepage" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
          <AppImage
            src="/assets/images/Lone_Star_Local_Full_Color-1771711306028.png"
            alt="The Lone Star Local logo"
            width={60}
            height={60}
            className="w-[55px] h-[55px] sm:w-[65px] sm:h-[65px]" />
          
        </Link>
        <nav className="hidden md:flex items-center gap-6">
          {navLinks.map((link) =>
          <Link
            key={link.href}
            href={link.href}
            className="text-white/70 hover:text-[#3dc3da] text-sm font-medium transition-colors duration-200 uppercase tracking-wide">
            
              {link.label}
            </Link>
          )}
        </nav>
        <Link
          href="/contact"
          className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 bg-[#3dc3da] text-white text-sm font-bold uppercase tracking-wide hover:bg-[#24b67b] transition-colors duration-300">
          
          Let's Talk
        </Link>
      </div>

      {/* Main content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 sm:px-8 py-16 sm:py-20">
        {/* 404 badge */}
        <div className="inline-block px-4 py-1.5 bg-[#f69d43] text-white text-xs font-bold uppercase tracking-widest mb-6 shadow-lg">
          Error 404
        </div>

        {/* Big 404 */}
        <div className="relative mb-4">
          <span
            className="block font-bold leading-none select-none"
            style={{
              fontSize: 'clamp(100px, 20vw, 220px)',
              color: 'transparent',
              WebkitTextStroke: '2px rgba(61,195,218,0.4)',
              letterSpacing: '-0.04em'
            }}>
            
            404
          </span>
          <span
            className="absolute inset-0 flex items-center justify-center font-bold leading-none"
            style={{
              fontSize: 'clamp(100px, 20vw, 220px)',
              color: 'rgba(61,195,218,0.08)',
              letterSpacing: '-0.04em'
            }}>
            
            404
          </span>
        </div>

        {/* Headline */}
        <h1 className="font-bold text-3xl sm:text-4xl md:text-5xl text-white text-center leading-tight mb-4 max-w-3xl">
          This Page Went Off the Grid.
        </h1>
        <p className="text-white/70 text-base sm:text-lg text-center max-w-xl mb-10 leading-relaxed">
          Happens to the best of us. The page you're looking for doesn't exist — but your next move does.
          Let's get you somewhere useful.
        </p>

        {/* Search bar */}
        <form onSubmit={handleSearch} className="w-full max-w-lg mb-10">
          <div className="flex items-stretch border-2 border-[#3dc3da]/60 hover:border-[#3dc3da] focus-within:border-[#3dc3da] transition-colors duration-200 bg-white/5 backdrop-blur-sm">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for services, work, about..."
              className="flex-1 bg-transparent text-white placeholder-white/40 px-5 py-4 text-sm outline-none" />
            
            <button
              type="submit"
              className="px-6 py-4 bg-[#3dc3da] text-white font-bold text-sm uppercase tracking-wide hover:bg-[#24b67b] transition-colors duration-300 flex items-center gap-2 min-h-[44px]">
              
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
              <span className="hidden sm:inline">Search</span>
            </button>
          </div>
          <p className="text-white/40 text-xs mt-2 text-center">Try: "branding", "web design", "portfolio", "contact"</p>
        </form>

        {/* Divider */}
        <div className="flex items-center gap-4 w-full max-w-lg mb-8">
          <div className="flex-1 h-px bg-white/15" />
          <span className="text-white/40 text-xs uppercase tracking-widest">Or jump to</span>
          <div className="flex-1 h-px bg-white/15" />
        </div>

        {/* Nav links grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 w-full max-w-2xl mb-12">
          {navLinks.map((link, i) => {
            const colors = ['#3dc3da', '#24b67b', '#f69d43', '#3dc3da', '#24b67b'];
            const color = colors[i % colors.length];
            return (
              <Link
                key={link.href}
                href={link.href}
                className="group flex flex-col items-center gap-2 px-4 py-4 border border-white/15 hover:border-opacity-100 bg-white/5 hover:bg-white/10 transition-all duration-300 text-center"
                style={{ '--hover-color': color } as React.CSSProperties}>
                
                <span className="text-xl">{link.icon}</span>
                <span
                  className="text-white/70 group-hover:text-white text-xs font-bold uppercase tracking-wide transition-colors duration-200">
                  
                  {link.label}
                </span>
                <div
                  className="w-0 group-hover:w-full h-0.5 transition-all duration-300"
                  style={{ backgroundColor: color }} />
                
              </Link>);

          })}
        </div>

        {/* CTA strip */}
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <Link
            href="/contact"
            className="group inline-flex items-center gap-3 px-8 py-4 bg-[#3dc3da] text-white font-bold text-sm uppercase tracking-wide hover:bg-[#24b67b] transition-all duration-300 shadow-lg hover:shadow-[#24b67b]/30 hover:shadow-xl transform hover:scale-105 min-h-[44px]">
            
            Start the Conversation
            <span className="group-hover:translate-x-1 transition-transform">→</span>
          </Link>
          <Link
            href="/homepage"
            className="inline-flex items-center gap-3 px-8 py-4 border-2 border-white/40 text-white font-bold text-sm uppercase tracking-wide hover:border-white hover:bg-white/10 transition-all duration-300 min-h-[44px]">
            
            ← Back to Home
          </Link>
        </div>
      </div>

      {/* Bottom accent bar */}
      <div className="relative z-10 h-1 w-full flex">
        <div className="flex-1 bg-[#3dc3da]" />
        <div className="flex-1 bg-[#24b67b]" />
        <div className="flex-1 bg-[#f69d43]" />
        <div className="flex-1 bg-[#351129] border-t border-white/20" />
      </div>
    </div>);

}